import { useAuth } from "@clerk/clerk-react";
import { Button, Grid, Link, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AddOnItem } from "schemas/dashboard";
import { routes } from "schemas/routes";

import Loading from "components/Loading";
import CopywritersFlow from "features/Auth/SelfServeVaOnboarding/TemplatesPick/CopywritersFlow";
import { fetcherAuth } from "utils/api";
import { identifyUser } from "utils/tracking";

import styles from "./styles";

const AddOnTemplatesIntakeForm = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { addOnItemId } = useParams();
  const { currentOrg, setCurrentOrg, setOrganizationVaProfile, setProfile } =
    useContext(OrganizationUserContext);
  const { setSubscription } = useContext(SubscriptionContext);

  const [displayCompleteScreen, setDisplayCompleteScreen] = useState(false);
  const [addOnItem, setAddonItem] = useState<AddOnItem | null>(null);

  const handleNext = async () => {
    setDisplayCompleteScreen(true);
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/payments-add-ons/${addOnItem?.id}/purchase-details`,
      "PATCH",
      {},
      {
        completed: true,
      },
    );
  };

  const [loading, setLoading] = useState(true);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      identifyUser(res.organizationUser.id);
      setCurrentOrg(res.organization);
      setProfile(res.organizationProfile);
      setOrganizationVaProfile(res.organizationVaProfile);
      setSubscription(res.subscription);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchasedItems = async () => {
    try {
      setLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/${addOnItemId}`,
        "GET",
      );
      if (res?.addOnItem?.completed) {
        setDisplayCompleteScreen(true);
      } else if (res.addOnItem) {
        setAddonItem(res.addOnItem);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentOrg?.id) {
      fetchUserInfo();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id && addOnItemId) {
      fetchPurchasedItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOnItemId, currentOrg?.id]);

  return loading ? (
    <Loading />
  ) : (
    <Grid container sx={styles.root}>
      <Grid sx={styles.formContainer} item xs={12}>
        {displayCompleteScreen && (
          <>
            <Typography variant="h3" textAlign={"center"} gutterBottom>
              You have completed all required steps! 🎉
            </Typography>

            <Typography textAlign={"center"} gutterBottom>
              Our copywriters will begin working on your template(s). You will
              receive an email notification when your templates are complete and
              you will be able to review it on the{" "}
              <Link href={`/${routes.templates}`}>Templates Dashboard</Link>.
            </Typography>

            <Grid container justifyContent={"center"}>
              <Button href={`/${routes.home}`} sx={{ mt: 2 }}>
                Go To Dashboard
              </Button>
            </Grid>
          </>
        )}
        {!displayCompleteScreen && addOnItem && (
          <CopywritersFlow
            addOnItemId={addOnItem?.id}
            handleNext={handleNext}
            showNichePickQuestion={currentOrg?.isVaUser}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default AddOnTemplatesIntakeForm;
